/**
 * Ticket variants: a ticketing item with dropdown modifiers (for example
 * "Hotel Shuttle" with a Hotel and a Time selection) is sold as one
 * permutation. Gates match token names as plain strings, so every sold
 * permutation mints its own token name. This module is the admin-app copy of
 * the naming rule. Keep it in step with `ronin-api/functions/ticketVariant.js`.
 *
 *   <base token name> | <option 1 label> | <option 2 label>
 *
 * The separator must never change once a permutation ticket has been sold.
 */

export const VARIANT_SEPARATOR = " | ";

/** Stored on `items_ticketing.modifiers` (jsonb array). */
export interface TicketModifierOption {
  label: string;
  /** Cents added to the ticket price when this option is chosen. */
  price: number;
}

export interface TicketModifierGroup {
  name: string;
  /** Tickets only support one choice per group (a dropdown). */
  type: "single";
  required: boolean;
  options: TicketModifierOption[];
}

/** One chosen option per group, in group order. Sent with each cart line. */
export interface SelectedTicketModifier {
  name: string;
  label: string;
  price: number;
}

const clean = (s: unknown): string => (typeof s === "string" ? s.trim() : "");

/** Parses the stored jsonb (or a form string) into a clean group list. */
export function parseTicketModifiers(raw: unknown): TicketModifierGroup[] {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = value.trim() ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map((g: any): TicketModifierGroup | null => {
      if (!g || typeof g !== "object") return null;
      const options = Array.isArray(g.options)
        ? g.options
            .map((o: any) => ({
              label: clean(o?.label),
              price: Math.round(Number(o?.price)) || 0,
            }))
            .filter((o: TicketModifierOption) => o.label)
        : [];
      return {
        name: clean(g.name),
        type: "single",
        required: g.required !== false,
        options,
      };
    })
    .filter((g): g is TicketModifierGroup => !!g && !!g.name && g.options.length > 0);
}

export function variantSuffix(labels: string[]): string {
  const parts = labels.map(clean).filter(Boolean);
  return parts.length ? VARIANT_SEPARATOR + parts.join(VARIANT_SEPARATOR) : "";
}

export function buildVariantName(baseName: string, labels: string[]): string {
  return clean(baseName) + variantSuffix(labels);
}

/** Every combination of one option per group, in group order. */
export function permutations(groups: TicketModifierGroup[]): string[][] {
  return groups.reduce<string[][]>(
    (acc, group) => acc.flatMap((prefix) => group.options.map((o) => [...prefix, o.label])),
    [[]]
  );
}

/**
 * All token names a ticket can mint: the base names as they are, plus one
 * permutation name per base name when the ticket has modifier groups.
 * `baseNames` are the keys of `token_payload` (ingress, egress, POS tokens).
 */
export function permutationTokenNames(
  baseNames: string[],
  groups: TicketModifierGroup[]
): string[] {
  const bases = baseNames.map(clean).filter(Boolean);
  if (groups.length === 0) return bases;
  const combos = permutations(groups);
  const out: string[] = [];
  for (const base of bases) {
    for (const labels of combos) out.push(buildVariantName(base, labels));
  }
  return out;
}

/** Number of permutations, for a warning in the editor. */
export function permutationCount(groups: TicketModifierGroup[]): number {
  return groups.reduce((n, g) => n * Math.max(g.options.length, 1), groups.length ? 1 : 0);
}

export function modifierTotal(selected: SelectedTicketModifier[]): number {
  return selected.reduce((sum, m) => sum + (Math.round(Number(m.price)) || 0), 0);
}
